import React, { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import ClassNames from "classnames";
import "./Textarea.scss";
import {
  Tooltip,
  FieldViewBlock,
  FormFieldError,
  getDataAnalyticsProps,
  IconButton,
  useCopyToClipboard,
} from "@SeemplicityDev/shared";

export interface TextareaErrorProps {
  message: string;
  type: string;
}

export interface TextareaProps {
  "data-testid"?: string;
  value?: string;
  // `type` is forwarded by callers (e.g. CreateTicketTemplateModal) and was
  // accepted by the original JS component's open prop contract; unused at runtime.
  type?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  name?: string;
  error?: TextareaErrorProps;
  wrapperClassName?: string;
  className?: string;
  label?: string;
  labelTop?: boolean;
  size?: string;
  required?: boolean;
  hideLabelHelper?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  password?: boolean;
  labelWidth?: boolean | string | number;
  isLarge?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  enableCopy?: boolean;
  title?: string;
  withHelp?: string | boolean;
  rowNumber?: number;
  resize?: string;
  viewMode?: boolean;
  noMargin?: boolean;
}

const Textarea: React.FC<TextareaProps> = (props) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const { copyLabel, copyText } = useCopyToClipboard();
  const {
    value,
    onChange = () => {},
    name,
    error,
    wrapperClassName,
    label,
    labelTop = false,
    size = "max",
    required = false,
    hideLabelHelper = false,
    disabled = false,
    readOnly = false,
    password,
    labelWidth = false,
    isLarge,
    placeholder = "",
    onBlur = () => {},
    onFocus = () => {},
    inputRef,
    enableCopy = false,
    title,
    "data-testid": dataTestId,
    withHelp = false,
    rowNumber = 2,
    resize = "none",
    viewMode = false,
  } = props;

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-text-area",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-password": password,
      "has-read-only": readOnly,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-large": isLarge,
      "has-error": !!error,
      "with-help": withHelp,
      "has-resize": resize,
      "has-copy": enableCopy,
    },
    props.className
  );

  const onFocusChange = () => {
    if (isFocused) {
      if (onBlur) {
        onBlur();
      }
    } else {
      if (onFocus) {
        onFocus();
      }
    }
    setIsFocused(!isFocused);
  };

  return (
    <div className={classNameWrapper}>
      {!viewMode ? (
        <>
          {label && (
            <label
              className={`input-label`}
              // `labelWidth` may be a boolean flag; coerce non-string/number
              // (and falsy) values to "" to match the original `labelWidth ? labelWidth : ""`.
              style={{
                width:
                  labelWidth && typeof labelWidth !== "boolean"
                    ? labelWidth
                    : "",
              }}
            >
              {t(label)}
              {!required && !hideLabelHelper && (
                <span className={"label-helper"}>Optional</span>
              )}
              {withHelp && (
                <>
                  <Tooltip type="info" content={withHelp}>
                    <i className={`seem-icon seem-icon-help`} />
                  </Tooltip>
                </>
              )}
            </label>
          )}

          <div className={`input-holder ${isFocused ? "is-focused" : ""}`}>
            {enableCopy && (
              <IconButton
                wrapperClassName={"copy"}
                data-testid={"copy"}
                type="button"
                onClick={() => {
                  copyText(value || "");
                }}
                withTooltip={{ content: copyLabel }}
                size={"xs"}
                buttonStyle={"secondary"}
                isIcon={"seem-icon-copy"}
              />
            )}
            <textarea
              style={{ whiteSpace: "preWrap" }}
              title={title}
              data-testid={`${dataTestId}-input`}
              ref={inputRef}
              readOnly={readOnly}
              placeholder={placeholder}
              name={name}
              rows={rowNumber}
              disabled={disabled}
              required={required}
              value={value || ""}
              onChange={(e) => {
                onChange(e);
              }}
              onBlur={onFocusChange}
              onFocus={onFocusChange}
              // `resize` is not a standard DOM attribute; passed through to preserve original runtime behavior
              {...{ resize }}
              {...getDataAnalyticsProps(props)}
            />
          </div>
          {error && (
            <FormFieldError
              errorMessage={error.message}
              errorType={error.type}
              isOpen={!isFocused}
              fieldName={dataTestId}
            />
          )}
        </>
      ) : (
        <FieldViewBlock label={label ?? ""} text={value} />
      )}
    </div>
  );
};

export default Textarea;
